import { useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import type { PaymentTier } from "../utils/tierOrder";

export interface PaymentTierStatus {
  tier: PaymentTier | null;
  attorneyHelplineMinTier: PaymentTier;
  isActive: boolean;
  isLoading: boolean;
  error: Error | null;
}

const FREE_STATUS: PaymentTierStatus = {
  tier: "free",
  attorneyHelplineMinTier: "basic",
  isActive: false,
  isLoading: false,
  error: null,
};

const DEV_TIER_STORAGE_KEY = "elayne-dev-tier-override";

function readDevTierFromStorage(): PaymentTier | null {
  if (!import.meta.env.DEV) return null;
  try {
    const raw = localStorage.getItem(DEV_TIER_STORAGE_KEY);
    if (raw === "free" || raw === "basic" || raw === "premium" || raw === "platinum") {
      return raw;
    }
  } catch {
    /* noop */
  }
  return null;
}

let devTierOverride: PaymentTier | null = readDevTierFromStorage();
let devTierListeners: Array<() => void> = [];

export function setDevTierOverride(tier: PaymentTier | null) {
  devTierOverride = tier;
  try {
    if (import.meta.env.DEV) {
      if (tier === null) {
        localStorage.removeItem(DEV_TIER_STORAGE_KEY);
      } else {
        localStorage.setItem(DEV_TIER_STORAGE_KEY, tier);
      }
    }
  } catch {
    /* noop */
  }
  devTierListeners.forEach((l) => l());
}

function subscribeDevTier(callback: () => void) {
  devTierListeners.push(callback);
  return () => {
    devTierListeners = devTierListeners.filter((l) => l !== callback);
  };
}

export function useDevTier(): PaymentTier | null {
  return useSyncExternalStore(
    subscribeDevTier,
    () => devTierOverride,
    () => null,
  );
}

export function usePaymentTier(estateId?: number | null): PaymentTierStatus {
  const { user, person } = useAppContext();
  const effectiveEstateId = estateId ?? person?.estate_id;
  const isElayneEmail = !!user?.email?.trim().toLowerCase().endsWith("@elayne.com");
  const devOverride = useDevTier();

  const { data, isLoading, error } = useQuery({
    queryKey: ["paymentTier", user?.user_id, effectiveEstateId],
    queryFn: async (): Promise<PaymentTierStatus> => {
      if (!user?.user_id || !effectiveEstateId) {
        return FREE_STATUS;
      }

      const { data: responseData, error: invokeError } = await supabase.functions.invoke(
        "billing/subscription/status",
        {
          body: { estateId: effectiveEstateId },
        },
      );

      if (invokeError) {
        throw new Error(`Failed to check payment tier: ${invokeError.message}`);
      }

      const tier: PaymentTier = responseData?.tier ?? "free";
      const attorneyHelplineMinTier: PaymentTier = responseData?.attorneyHelplineMinTier ?? "basic";

      // Internal accounts always have at least basic access
      const effectiveTier: PaymentTier = isElayneEmail && tier === "free" ? "basic" : tier;

      return {
        tier: effectiveTier,
        attorneyHelplineMinTier,
        isActive: effectiveTier !== "free",
        isLoading: false,
        error: null,
      };
    },
    enabled: !!user?.user_id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount) => failureCount < 2,
    refetchOnWindowFocus: false,
  });

  if (devOverride) {
    return {
      tier: devOverride,
      attorneyHelplineMinTier: data?.attorneyHelplineMinTier ?? "basic",
      isActive: devOverride !== "free",
      isLoading,
      error: null,
    };
  }

  return data || { ...FREE_STATUS, isLoading, error: error as Error | null };
}
